// App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme from './theme';

//Components
import Layout from './components/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import MarineMooringPage from './pages/MarineMooringPage';
import SensorPackDetailPage from './pages/SensorPackDetailPage';
import GridDetailPage from './pages/GridDetailPage';
import PondsListPage from './pages/PondsListPage'; // New component for multiple ponds
import MooringRollupPage from './pages/MooringRollupPage';
import HQBuoysPage from './pages/HQBuoysPage';
import HABDashboardPage from './pages/HABDashboardPage';
import AccountSettingsPage from './pages/AccountSettingsPage';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Admin-only route — redirects non-admins to home
const AdminRoute = ({ children }) => {
  const { isLoggedIn, loading, user } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Smart routing component for "/" and unknown paths
const SmartRedirect = () => {
  const { isLoggedIn, loading } = useAuth();

  // Don't redirect until auth is resolved
  if (loading) {
    return <div>Loading...</div>;
  }

  // If not logged in, go to login
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  // All authenticated users land on the ponds list. PondsListPage itself
  // filters by is_pond and handles the single- vs multiple-grid cases, so we
  // don't gate the redirect on grid loading or a name-based pond heuristic.
  return <Navigate to="/ponds" replace />;
};

// Layout wrapper component
const LayoutWrapper = ({ children }) => (
  <Layout>{children}</Layout>
);

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Pond Routes */}
      <Route
        path="/ponds"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <PondsListPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ponds/:grid-id"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <HABDashboardPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />

      {/* Account settings */}
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <AccountSettingsPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />

      {/* Existing Routes */}
      <Route
        path="/moorings/:mooring_id/sensor-packs/:sensor_pack_id"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <SensorPackDetailPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sensor-packs/:id"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <SensorPackDetailPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/grids/:id"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <GridDetailPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="grids/:grid-id/moorings/:mooring-id"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <MarineMooringPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />

      {/* Legacy pond-moorings route - redirect to new structure */}
      <Route
        path="/pond-moorings/:id"
        element={<Navigate to="/ponds/:id" replace />}
      />
      <Route
        path="/mooring-data/:mooringId/"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <MooringRollupPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />

      {/* HQ Buoys — admin only */}
      <Route
        path="/hq-buoys"
        element={
          <AdminRoute>
            <LayoutWrapper>
              <HQBuoysPage />
            </LayoutWrapper>
          </AdminRoute>
        }
      />

      {/* Smart default route */}
      <Route path="/" element={<SmartRedirect />} />

      {/* Wildcard route */}
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;